/**
 * @description Tooltip component
 * @file
 * @copyright © Copyright IBI Group., created 2019. All use, disclosure, and/or reproduction of this material is prohibited unless authorized in writing. All rights reserved.
 * @module components/Tooltip
 */

import React from 'react';
import PropTypes from 'prop-types';

/**
 * @description The Tooltip component.
 * @class
 * @extends {React.Component}
 */
class Tooltip extends React.Component {

    /**
     * @inheritdoc
     */
    constructor(props) {
        super(props);
        this.ref = React.createRef();
        this.state = {
            top: 0,
            left: 0
        };
    }

    /**
     * @inheritdoc
     */
    shouldComponentUpdate(nextProps, nextState) {
        const hasNewEventTarget =
            (nextProps.event && !this.props.event) ||
            (nextProps.event &&
                nextProps.event.target !== this.props.event.target);
        
        return (
            hasNewEventTarget ||
            this.props.show !== nextProps.show ||
            this.state.left !== nextState.left ||
            this.state.top !== nextState.top
        );
    }

    /**
     * @inheritdoc
     */
    componentDidUpdate(prevProps) {
        const hasNewEventTarget =
            (!prevProps.event && this.props.event) ||
            (this.props.event &&
                prevProps.event.target !== this.props.event.target);

        if (hasNewEventTarget && this.props.show) {
            this.setState(this.getTooltipPosition());
        }
    }

    /**
     * @inheritdoc
     */
    render() {
        return (
            this.props.show && (
                <div ref={this.ref} className='Tooltip' style={this.state}>
                    {this.getTooltipContent()}
                </div>
            )
        );
    }

    /**
     * @description Returns the default content for the tooltip - an empty string.
     * @returns {string} - an empty string.
     */
    getTooltipContent() {
        return '';
    }

    /**
     * @description Gets the position that the tooltip should be located in.
     * @returns {object} The position.
     */
    getTooltipPosition() {
        const elementBoundingBox = this.props.event.target.getBoundingClientRect(),
            appContentBoundingBox = document
                .querySelector('.App .content')
                .getBoundingClientRect(),
            tooltipSize = {
                width: this.ref.current.clientWidth + 10,
                height: this.ref.current.clientHeight + 20
            },
            adjustedElBoundingBox = {
                top: elementBoundingBox.top - appContentBoundingBox.top,
                bottom: elementBoundingBox.bottom - appContentBoundingBox.top,
                left: elementBoundingBox.left - appContentBoundingBox.left
            };

        let top = Math.min(
                appContentBoundingBox.height - tooltipSize.height,
                adjustedElBoundingBox.bottom
            ),
            left = Math.min(
                appContentBoundingBox.width - tooltipSize.width,
                adjustedElBoundingBox.left
            ),
            bottom = top + tooltipSize.height;

        // Calculate if tooltip will cover bounding box.
        if (
            (bottom > adjustedElBoundingBox.top &&
                bottom < adjustedElBoundingBox.bottom) ||
            (top > adjustedElBoundingBox.top &&
                top < adjustedElBoundingBox.bottom) ||
            (top < adjustedElBoundingBox.top &&
                bottom > adjustedElBoundingBox.bottom)
        ) {
            top = adjustedElBoundingBox.top - tooltipSize.height;
        }

        return {
            top: top + 10,
            left: left
        };
    }
}

Tooltip.defaultProps = {
    show: false,
    event: {}
};

Tooltip.propTypes = {
    show: PropTypes.bool.isRequired,
    event: PropTypes.object.isRequired
};

export default Tooltip;
